'use client';

import { Paperclip } from 'lucide-react';

import { isImageAttachmentPath, isVideoAttachmentPath } from './report-labels';

/**
 * Renders one bug-report attachment: an inline <video> for videos, an <img>
 * thumbnail for images, and a labelled download link for everything else
 * (pdf/doc/docx). Detection is by the stored path's extension. Shared by the
 * reporter thread (my-reports-list) and the admin triage thread
 * (bug-reports-table) so both surfaces render every file type consistently.
 * Returns null when the signed URL is missing (e.g. it failed to mint).
 */
export function AttachmentThumbnail({
  path,
  signedUrl,
}: {
  path: string;
  signedUrl: string | null | undefined;
}) {
  if (!signedUrl) return null;

  if (isVideoAttachmentPath(path)) {
    return (
      <video
        src={signedUrl}
        controls
        playsInline
        preload="metadata"
        className="max-h-48 w-full max-w-xs rounded border"
      />
    );
  }

  if (isImageAttachmentPath(path)) {
    return (
      <a
        href={signedUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="block"
      >
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src={signedUrl}
          alt=""
          className="h-16 w-16 rounded border object-cover"
        />
      </a>
    );
  }

  // Documents (pdf/doc/docx) and any other type: a labelled download link.
  const filename = path.split('/').pop() || 'attachment';

  return (
    <a
      href={signedUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="hover:bg-muted/50 flex max-w-[12rem] items-center gap-1.5 rounded border px-2 py-1.5 text-xs"
      title={filename}
    >
      <Paperclip className="text-muted-foreground h-3.5 w-3.5 shrink-0" />
      <span className="truncate">{filename}</span>
    </a>
  );
}
